import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, Navigation, Autoplay } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'

import '../component/Featuredheat.css'

import Wwe from '../images/icons/10.png'
import F1 from '../images/icons/9.png'
import Uefa from '../images/icons/1.png'
import Ufc from '../images/icons/6.png'
import apiClient, { resolveAssetUrl } from '../services/apiClient'
import { navigateTo } from '../app/routes'

const categoryIcons = [Wwe, F1, Uefa, Ufc]

function formatMoney(value) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value || 0)
}

function getAuctionDisplayStatus(item, now = Date.now()) {
  const startsAt = item?.startsAt ? new Date(item.startsAt).getTime() : NaN
  const endsAt = item?.endsAt ? new Date(item.endsAt).getTime() : NaN

  if (item?.status === 'scheduled' && Number.isFinite(startsAt) && startsAt <= now) {
    return Number.isFinite(endsAt) && endsAt <= now ? 'closed' : 'live'
  }

  if (item?.status === 'live' && Number.isFinite(endsAt) && endsAt <= now) {
    return 'closed'
  }

  return item?.status || 'closed'
}

function getTimeParts(item, now = Date.now()) {
  const status = getAuctionDisplayStatus(item, now)
  const targetValue = status === 'scheduled' ? item.startsAt : item.endsAt
  const target = new Date(targetValue)
  const diff = Number.isNaN(target.getTime()) ? 0 : Math.max(0, target.getTime() - now)
  const totalSeconds = Math.floor(diff / 1000)

  return {
    hours: String(Math.floor(totalSeconds / 3600)).padStart(2, '0'),
    minutes: String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0'),
    seconds: String(totalSeconds % 60).padStart(2, '0'),
  }
}

function mapFeaturedFeed(feed) {
  const auctions = (feed?.auctions || []).map((auction, index) => ({
    id: auction.id,
    kind: 'auction',
    status: auction.status,
    startsAt: auction.startsAt,
    endsAt: auction.endsAt,
    image: auction.images?.[0]?.url ? resolveAssetUrl(auction.images[0].url) : '',
    icon: categoryIcons[index % categoryIcons.length],
    name: auction.title,
    category: auction.category || 'Auction',
    badge: auction.status === 'live' ? 'Live Auction' : 'Scheduled Auction',
    currentBid: formatMoney(auction.currentBid),
    market: auction.reservePrice ? `${formatMoney(auction.reservePrice)} reserve` : 'No reserve',
    bidders: `${auction.watchlistedBy?.length || 0} Watchers`,
    bids: `${auction.bidCount || 0} Bids`,
    route: `/auctions/${auction.id}`,
    action: 'Place Bid',
  }))

  const listings = (feed?.listings || []).map((listing, index) => ({
    id: listing.id,
    kind: 'listing',
    image: listing.images?.[0]?.url ? resolveAssetUrl(listing.images[0].url) : '',
    icon: categoryIcons[(index + auctions.length) % categoryIcons.length],
    name: listing.title,
    category: listing.category || 'Buy Now',
    badge: 'Buy Now Spotlight',
    currentBid: formatMoney(listing.price),
    market: `${listing.inventory || 0} available`,
    bidders: listing.bundleEligible ? 'Bundle Eligible' : 'Shop Item',
    bids: listing.quickCheckout ? 'Quick Checkout' : 'Standard Checkout',
    route: `/shop/${listing.id}`,
    action: 'Buy Now',
    time: { hours: 'BUY', minutes: 'NOW', seconds: 'OBC' },
  }))

  return [...auctions, ...listings]
}

function Featuredheat() {
  const prevRef = useRef(null)
  const nextRef = useRef(null)
  const [liveCards, setLiveCards] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  useEffect(() => {
    let mounted = true

    apiClient.featuredCatalog()
      .then((feed) => {
        if (mounted) {
          setLiveCards(mapFeaturedFeed(feed))
          setIsLoading(false)
        }
      })
      .catch(() => {
        if (mounted) {
          setLiveCards([])
          setIsLoading(false)
        }
      })

    return () => {
      mounted = false
    }
  }, [])

  const displayedCards = liveCards.map((item) => {
    if (item.kind !== 'auction') return item

    const status = getAuctionDisplayStatus(item, now)

    return {
      ...item,
      badge: status === 'live' ? 'Live Auction' : status === 'scheduled' ? 'Scheduled Auction' : 'Auction Closed',
      time: getTimeParts(item, now),
    }
  })
  const repeatedCards = useMemo(() => {
    if (isLoading) return []
    if (displayedCards.length >= 4) return [...displayedCards, ...displayedCards]
    return [...displayedCards, ...displayedCards, ...displayedCards]
  }, [displayedCards, isLoading])

  return (
    <section className="fh-section">
      <div className="fh-container">
        <div className="fh-heading">
          <span className="fh-eyebrow">High-value collectibles</span>

          <h2 className="fh-title">Tonight&apos;s Featured Heat</h2>

          <p className="fh-subtitle">
            Step onto the auction floor and battle for selected sports and entertainment collectibles.
            Every featured card is controlled by OBC operations from the admin dashboard.
          </p>
        </div>

        <div className="fh-slider-area">
          <button ref={prevRef} className="fh-arrow fh-arrow-left" type="button" aria-label="Previous featured item">
            &lt;
          </button>

          <button ref={nextRef} className="fh-arrow fh-arrow-right" type="button" aria-label="Next featured item">
            &gt;
          </button>

          {isLoading ? (
            <div className="fh-skeleton-grid" aria-live="polite" aria-busy="true">
              {Array.from({ length: 4 }, (_, index) => (
                <FeaturedHeatSkeleton key={`featured-skeleton-${index}`} />
              ))}
            </div>
          ) : repeatedCards.length ? (
            <Swiper
              modules={[Pagination, Navigation, Autoplay]}
              spaceBetween={26}
              slidesPerView={1}
              loop={repeatedCards.length > 1}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              pagination={{
                clickable: true,
              }}
              navigation={{
                prevEl: prevRef.current,
                nextEl: nextRef.current,
              }}
              onBeforeInit={(swiper) => {
                swiper.params.navigation.prevEl = prevRef.current
                swiper.params.navigation.nextEl = nextRef.current
              }}
              breakpoints={{
                576: {
                  slidesPerView: 2,
                },
                992: {
                  slidesPerView: 3,
                },
                1280: {
                  slidesPerView: 4,
                },
              }}
              className="fh-swiper"
            >
              {repeatedCards.map((item, index) => (
              <SwiperSlide key={`${item.id}-${index}`}>
                <div className="fh-card">
                  <div className="fh-img-box">
                    <span className="fh-badge">{item.badge}</span>
                    {item.image ? <img src={item.image} alt={item.name} className="fh-card-img" /> : <span className="fh-card-img fh-card-img--placeholder">No uploaded image</span>}
                  </div>

                  <div className="fh-icon-box">
                    <img src={item.icon} alt={item.category} />
                  </div>

                  <h3 className="fh-card-name">{item.name}</h3>

                  <div className="fh-info">
                    <span>{item.action === 'Buy Now' ? 'Price' : 'Current Bid'}: {item.currentBid}</span>
                    <span>{item.market}</span>
                  </div>

                  <div className="fh-info fh-info-second">
                    <span>{item.bidders}</span>
                    <span>{item.bids}</span>
                  </div>

                  <div className="fh-timer">
                    <div>
                      <strong>{item.time.hours}</strong>
                      <small>Hrs</small>
                    </div>
                    <div>
                      <strong>{item.time.minutes}</strong>
                      <small>Mins</small>
                    </div>
                    <div>
                      <strong>{item.time.seconds}</strong>
                      <small>Sec</small>
                    </div>
                  </div>

                  <div className="fh-buttons">
                    <button type="button" onClick={() => { navigateTo(item.route) }}>{item.action}</button>
                    <button type="button" onClick={() => { navigateTo('/register') }}>Join OBC</button>
                  </div>
                </div>
              </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className="fh-empty">Featured items will appear here when OBC operations publishes live lots or shop spotlights.</div>
          )}
        </div>
      </div>
    </section>
  )
}

function FeaturedHeatSkeleton() {
  return (
    <div className="fh-card fh-card--skeleton" aria-hidden="true">
      <span className="fh-skeleton-block fh-skeleton-image" />
      <span className="fh-skeleton-icon" />
      <span className="fh-skeleton-line is-title" />
      <span className="fh-skeleton-line" />
      <span className="fh-skeleton-line is-short" />
      <div className="fh-timer">
        <div><span className="fh-skeleton-line" /></div>
        <div><span className="fh-skeleton-line" /></div>
        <div><span className="fh-skeleton-line" /></div>
      </div>
    </div>
  )
}

export default Featuredheat
